import * as React from "react";
import { Button } from "@utdanningsdirektoratet/button";
import { useId } from "@utdanningsdirektoratet/utils/useId";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { Icon, SmallIcon } from "@utdanningsdirektoratet/icon";
import { capitalize } from "@utdanningsdirektoratet/utils/string";
import { useMergeRefs } from "@utdanningsdirektoratet/utils/react";
import styles from "./Dropdown.module.less";

type DropdownProps = {
  lists: Array<Array<React.ReactElement>>;
  align?: "left" | "right";
  placement?: "top" | "bottom";
  icon?: SmallIcon | "none";
  children?: React.ReactNode;
} & React.ComponentProps<typeof Button>;

const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ icon = "angle down", children, lists, align = "right", placement = "bottom", className, ...rest }, ref) => {
    const dropdown = React.useRef<HTMLDivElement>(null);
    const id = useId();
    const [isOpen, setIsOpen] = React.useState(false);

    const toggleOpen = (): void => {
      setIsOpen((prevState) => !prevState);
    };

    React.useEffect(() => {
      const handleClickOutside = (e: MouseEvent): void => {
        if (
          ((e.target as Element).closest("li") as Element)?.getAttribute("data-closeonclick") ||
          !dropdown.current?.contains(e.target as Element)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen]);

    const dropDownLists = lists.map((list, i) => {
      if (list.length === 0) return null;
      return (
        <ul key={`Dropdown-${id}-${i}`} className={styles.dropdownList}>
          {list.map((item, j) =>
            React.cloneElement(item as React.ReactElement, {
              key: `Dropdown-${id}-${i}-${j}`,
            })
          )}
        </ul>
      );
    });

    const buttonClass = cls({
      [styles.dropdownButton]: true,
      [styles.dropdownButtonActive]: isOpen,
      [styles.dropdownButtonEmpty]: !children,
      [styles.dropdownButtonSmall]: rest.size === "small",
    });

    const dropdownContainerClass = cls({
      [styles.dropdownContainer]: true,
      [styles.dropdownContainerVisible]: isOpen,
      [styles.dropdownContainerSmall]: rest.size === "small",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [styles[`dropdownContainer${capitalize(align)}`]]: true,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      [styles[`dropdownContainer${capitalize(placement)}`]]: true,
    });

    const iconCLass = cls({
      [styles.dropdownButtonIcon]: true,
      [styles.dropdownButtonIconActive]: isOpen && icon === "angle down",
      [styles.dropdownButtonIconEmpty]: !children,
    });

    const dropdownClass = cls(styles.dropdown, className);

    return (
      <div ref={useMergeRefs([dropdown, ref])} role="presentation" tabIndex={-1} className={dropdownClass}>
        <Button {...rest} className={buttonClass} aria-expanded={isOpen} aria-haspopup="true" aria-controls={id} onClick={toggleOpen} last>
          {children}
          <Icon className={iconCLass} icon={icon} />
        </Button>
        <div className={dropdownContainerClass} aria-hidden={!isOpen} id={id}>
          {dropDownLists}
        </div>
      </div>
    );
  }
);

export default Dropdown;
